import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, Users, Layers, Award, ShieldCheck } from "lucide-react";
import { BrandBadge } from "@/components/BrandBadge";
import { COMPANY } from "@/lib/company";

const TECH = [
  { group: "Modern Cloud & SaaS", items: ["Salesforce", "AWS", "MuleSoft", "IBM Cloud"] },
  { group: "Enterprise Integration", items: ["IBM App Connect Enterprise", "IBM MQ", "IBM WebSphere", "Web Services"] },
  { group: "Engineering Foundations", items: ["Java", "J2EE", "IBM Mainframes", "Advantage Gen (4GL)"] },
];

const VALUES = [
  {
    icon: GraduationCap,
    title: "Learn by building, not by watching",
    text: "Every cohort ships real projects. Concepts are introduced only when a project demands them, so your resume tells a coherent story from day one.",
  },
  {
    icon: Users,
    title: "Small cohorts, direct access",
    text: "We deliberately keep batches small. You will speak to the trainer in every session, ask questions freely, and receive personal feedback on your work.",
  },
  {
    icon: Layers,
    title: "Four decades of context, one honest opinion",
    text: "We have lived through mainframes, client-server, J2EE, integration middleware, cloud and now AI. That perspective helps us tell you which credentials genuinely open doors — and which do not.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">About {COMPANY.name}</div>
          <h1 className="mt-4 font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-slate-950 leading-[0.95] max-w-4xl">
            A training practice built on twenty years of real consulting work.
          </h1>
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed max-w-3xl">
            {COMPANY.name} is an independent IT training and consulting practice based in Bangalore.
            We coach students, working professionals and career switchers into certified roles in
            Salesforce, AWS, MuleSoft, IBM and AI. Our teaching draws directly from
            projects we have delivered for global clients — not from generic slide decks.
          </p>
        </div>
      </section>

      {/* Our story */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Our story</div>
          <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl text-slate-950">
            Why the name HCS.
          </h2>
        </div>
        <div className="lg:col-span-7 text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
          <p>
            HCS stands for <em>Himani Computer Services</em>. Himani was a seven-year-old girl in
            her first year of primary school in Bangalore during 2017–18. In March 2018 her father
            travelled to the United States on a business trip. She was kidnapped from Bangalore in
            April 2018 by kidnappers who took her to a remote place in Karnataka, demanded ransom,
            attacked her, and made her untraceable since June 2020.
          </p>
          <p>
            Her father returned to Bangalore in May 2018 and has been searching for his daughter
            ever since. She has remained untraceable since June 2020. He worked on consulting
            projects from 2021 to 2023, and has been building this IT training and consulting
            practice since January 2024 — in her honour.
          </p>
          <p>
            The suffix <b>AI</b> reflects where the industry is now heading. Our training and
            consulting increasingly touch AI-adjacent work: MuleSoft AI connectors, Salesforce
            Einstein, and AWS. Adding <b>AI</b> to the name simply keeps the brand honest about
            what we teach today.
          </p>
        </div>
      </section>

      {/* Meet the founder */}
      <section className="border-t border-slate-200 bg-slate-50/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Meet the founder</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl text-slate-950">
              Gurudeva K B.
            </h2>
            <p className="mt-2 text-sm text-slate-500 font-mono-plex uppercase tracking-widerest">
              Founder & Principal Trainer
            </p>
            <div className="mt-6 border border-slate-200 bg-white p-6">
              <div className="text-[11px] uppercase tracking-widerest text-slate-500 font-mono-plex">Certifications</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-800">
                <li className="flex items-center gap-2"><Award className="w-4 h-4 text-[#0052FF]" /> PMP — Project Management Professional</li>
                <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0052FF]" /> ITIL — Service Management</li>
                <li className="flex items-center gap-2"><BrandBadge vendor="Salesforce" size={16} /> Salesforce certified</li>
                <li className="flex items-center gap-2"><BrandBadge vendor="AWS" size={16} /> AWS certified</li>
              </ul>
            </div>
          </div>
          <div className="lg:col-span-8 text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              Gurudeva K B has worked in enterprise IT — including ten years across the United
              States and Singapore — with domain experience in Healthcare, Financial Services and
              Energy. That career has spanned shipping production systems on IBM mainframes,
              administering the
              <span className="whitespace-nowrap"> Advantage Gen </span>
              (a 4GL tool that predates Salesforce) Encyclopedia across ten application systems
              for over five years, evaluating and migrating applications onto cloud platforms
              (AWS, Azure and Salesforce), and now helping enterprises adopt AI responsibly.
            </p>
            <p>
              His consulting career spans Salesforce, MuleSoft, IBM WebSphere, IBM App Connect Enterprise,
              IBM MQ, Java, J2EE, Web Services, and AWS. That range is deliberate. It lets him teach
              a topic in context — for example, explaining <em>why</em> API-led connectivity matters by
              showing what point-to-point integrations used to cost enterprises in the WebSphere era.
            </p>
            <p>
              Outside consulting work, Gurudeva mentored fellow students at engineering college
              during his own college days, and has since coached working professionals through
              career transitions.
            </p>
            <p>
              He believes many employers today lay off staff for a lack of skills in newer
              technologies — AI, Salesforce, AWS and MuleSoft. Those professionals can be
              retrained by {COMPANY.name} in these technologies in just a few months for a
              modest training fee, and become re-employment ready — rather than staying unemployed
              for years or being transitioned out of IT altogether.
            </p>
            <p>
              The mission of {COMPANY.name} is to retrain such candidates and solve the
              problem of unemployment for experienced IT professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">How we teach</div>
          <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl text-slate-950">
            Three principles that shape every cohort.
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <div
                key={i}
                data-testid={`about-value-${i}`}
                className="border border-slate-200 bg-white p-6 md:p-8 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <Icon className="w-8 h-8 text-[#0052FF]" />
                <h3 className="mt-6 font-display font-semibold text-xl text-slate-950">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tech breadth */}
      <section className="border-t border-slate-200 bg-slate-50/60">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-24">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Consulting depth</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl text-slate-950">
              Two decades of hands-on work across the enterprise stack.
            </h2>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
              Every technology we teach today, we have shipped in production for a real client.
              That is why our lessons feel practical rather than academic.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TECH.map((t) => (
              <div key={t.group} className="border border-slate-200 bg-white p-6">
                <div className="text-[11px] uppercase tracking-widerest text-slate-500 font-mono-plex">{t.group}</div>
                <ul className="mt-4 space-y-2 text-sm text-slate-800">
                  {t.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-[#0052FF] shrink-0">›</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Startup / partner note */}
      <section className="border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-widerest text-slate-500 font-mono-plex">Invest · Partner · Teach</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl text-slate-950">
              We are in startup mode.
            </h2>
          </div>
          <div className="lg:col-span-7 text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
            <p>
              {COMPANY.name} is currently in startup mode and is actively seeking funding
              partners, co-founders and expert trainers who share our mission — retraining
              experienced IT professionals into today&apos;s high-demand roles across Salesforce,
              AWS, MuleSoft, IBM and AI.
            </p>
            <p>
              If you would like to invest, partner, or teach a course with us, we would love to
              hear from you.
            </p>
            <div className="pt-2 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="about-partner-contact-btn"
                className="inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] text-white font-medium px-6 py-3 transition-colors"
              >
                Get in touch <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href={`mailto:${COMPANY.email}?subject=Partnership%20/%20investment%20enquiry`}
                data-testid="about-partner-email-btn"
                className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-medium px-6 py-3 transition-colors"
              >
                Email us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-widerest text-slate-400 font-mono-plex">Ready when you are</div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl leading-tight">
              See a live class before you commit to a course.
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl text-base leading-relaxed">
              Reserve a demo seat for ₹500 (India) or ${15} (International). Meet the trainer,
              walk through a real project, and get a personalised career plan.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/pay-demo"
              data-testid="about-cta-pay-demo"
              className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-[#FACC15] font-medium px-6 py-4 transition-colors"
            >
              Book a demo <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
